import {
  ActionIcon,
  Alert,
  AppShell,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Code,
  Container,
  Group,
  Loader,
  Modal,
  NumberInput,
  SegmentedControl,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { notifications } from "@mantine/notifications"
import { IconGift, IconRefresh, IconSearch, IconTrash } from "@tabler/icons-react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"

import { Navbar } from "@/components/Navbar"
import { getServiceUrl, MONITORED_SERVICES, type ServiceId } from "@/config/services"
import { supabase } from "@/lib/supabase"

type EnvironmentTarget = "development" | "production"

type UserListRow = {
  id: string
  email: string | null
  subscriptionTier: string | null
  subscriptionStatus: string | null
  role: string | null
  giftedBy: string | null
  giftedTier: string | null
  giftedUntil: string | null
  hasActiveGift: boolean
  createdAt: string | null
}

type UserListResponse = {
  users: UserListRow[]
  total: number
  limit: number
  offset: number
}

const SERVICE_ID: ServiceId = "agent-gate"
const PAGE_SIZE = 25

const authedFetch = async (target: EnvironmentTarget, path: string, init?: RequestInit): Promise<Response> => {
  const { data, error } = await supabase.auth.getSession()
  if (error || !data.session) {
    throw new Error("Not signed in. Sign in with an admin account first.")
  }
  const token = data.session.access_token
  const url = getServiceUrl(SERVICE_ID, target === "production")
  return fetch(`${url}${path}`, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  })
}

const fetchUsers = async (target: EnvironmentTarget, search: string, offset: number): Promise<UserListResponse> => {
  const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) })
  if (search.length > 0) params.set("search", search)
  const response = await authedFetch(target, `/admin/users?${params.toString()}`)
  if (!response.ok) {
    const text = await response.text()
    throw new Error(`Users fetch failed: ${response.status} ${response.statusText} ${text}`)
  }
  return response.json() as Promise<UserListResponse>
}

const grantGift = async (
  target: EnvironmentTarget,
  userId: string,
  durationDays: number,
  reason: string,
): Promise<void> => {
  const response = await authedFetch(target, `/admin/users/${userId}/gift`, {
    method: "POST",
    body: JSON.stringify({ durationDays, reason: reason.trim() || undefined }),
  })
  if (!response.ok) {
    const text = await response.text()
    throw new Error(`Grant failed: ${response.status} ${response.statusText} ${text}`)
  }
}

const revokeGift = async (target: EnvironmentTarget, userId: string): Promise<void> => {
  const response = await authedFetch(target, `/admin/users/${userId}/gift`, { method: "DELETE" })
  if (!response.ok) {
    const text = await response.text()
    throw new Error(`Revoke failed: ${response.status} ${response.statusText} ${text}`)
  }
}

const formatDate = (iso: string | null): string => {
  if (!iso) return "—"
  const d = new Date(iso)
  return Number.isFinite(d.getTime()) ? d.toLocaleDateString() : iso
}

const formatDateTime = (iso: string | null): string => {
  if (!iso) return "—"
  const d = new Date(iso)
  return Number.isFinite(d.getTime()) ? d.toLocaleString() : iso
}

const tierBadgeColor = (tier: string | null, hasActiveGift: boolean): string => {
  if (hasActiveGift) return "violet"
  if (tier === "pro") return "blue"
  return "gray"
}

export function UsersPage() {
  const [target, setTarget] = useState<EnvironmentTarget>("development")
  const [searchInput, setSearchInput] = useState("")
  const [search, setSearch] = useState("")
  const [offset, setOffset] = useState(0)
  const [grantTarget, setGrantTarget] = useState<UserListRow | null>(null)
  const [grantDays, setGrantDays] = useState<number>(30)
  const [grantReason, setGrantReason] = useState("")
  const [grantModalOpen, { open: openGrantModal, close: closeGrantModal }] = useDisclosure(false)
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ["admin-users", target, search, offset],
    queryFn: () => fetchUsers(target, search, offset),
    staleTime: 15_000,
    retry: 1,
  })

  const grantMutation = useMutation({
    mutationFn: ({ userId, durationDays, reason }: { userId: string; durationDays: number; reason: string }) =>
      grantGift(target, userId, durationDays, reason),
    onSuccess: () => {
      notifications.show({ title: "Gift granted", message: "Pro access applied.", color: "green" })
      closeGrantModal()
      setGrantReason("")
      void queryClient.invalidateQueries({ queryKey: ["admin-users", target] })
    },
    onError: (err) => {
      notifications.show({ title: "Grant failed", message: err.message, color: "red" })
    },
  })

  const revokeMutation = useMutation({
    mutationFn: (userId: string) => revokeGift(target, userId),
    onSuccess: () => {
      notifications.show({ title: "Gift revoked", message: "User reverts to base tier.", color: "green" })
      void queryClient.invalidateQueries({ queryKey: ["admin-users", target] })
    },
    onError: (err) => {
      notifications.show({ title: "Revoke failed", message: err.message, color: "red" })
    },
  })

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSearch(searchInput.trim())
    setOffset(0)
  }

  const handleClearSearch = () => {
    setSearchInput("")
    setSearch("")
    setOffset(0)
  }

  const openGrantFor = (user: UserListRow) => {
    setGrantTarget(user)
    setGrantDays(30)
    setGrantReason("")
    openGrantModal()
  }

  const handleConfirmGrant = () => {
    if (!grantTarget) return
    grantMutation.mutate({ userId: grantTarget.id, durationDays: grantDays, reason: grantReason })
  }

  const handleRevoke = (user: UserListRow) => {
    const ok = window.confirm(`Revoke active gift for ${user.email ?? user.id}?`)
    if (!ok) return
    revokeMutation.mutate(user.id)
  }

  const handleRefresh = () => {
    void query.refetch()
  }

  const targetUrl = MONITORED_SERVICES[SERVICE_ID][target === "production" ? "productionUrl" : "developmentUrl"]
  const { data } = query
  const rows = data?.users ?? []
  const total = data?.total ?? 0
  const hasPagination = search.length === 0 && total > rows.length + offset

  return (
    <AppShell padding="md">
      <Navbar onRefresh={handleRefresh} isRefreshing={query.isFetching} />

      <AppShell.Main>
        <Container size="xl" py="md">
          <Group justify="space-between" mb="lg">
            <Box>
              <Title order={2}>Users</Title>
              <Text c="dimmed" size="sm">
                Search by email or page through recent signups. Grant or revoke comp Pro from this page — no Stripe.
              </Text>
            </Box>
            <Group>
              <SegmentedControl
                value={target}
                onChange={(v) => setTarget(v as EnvironmentTarget)}
                data={[
                  { label: "dev", value: "development" },
                  { label: "prod", value: "production" },
                ]}
              />
              <Button
                leftSection={<IconRefresh size={16} />}
                onClick={handleRefresh}
                loading={query.isFetching}
                variant="default"
              >
                Refresh
              </Button>
            </Group>
          </Group>

          <Card withBorder padding="md" radius="md" mb="md">
            <form onSubmit={handleSearchSubmit}>
              <Group>
                <TextInput
                  placeholder="Search by exact email…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.currentTarget.value)}
                  leftSection={<IconSearch size={16} />}
                  style={{ flex: 1 }}
                />
                <Button type="submit" variant="filled">
                  Search
                </Button>
                {search && (
                  <Button variant="subtle" onClick={handleClearSearch}>
                    Clear
                  </Button>
                )}
                <Text size="xs" c="dimmed">
                  Backend: <Code>{targetUrl}</Code>
                </Text>
              </Group>
            </form>
          </Card>

          {query.isLoading && (
            <Center py="xl">
              <Box ta="center">
                <Loader size="lg" mb="md" />
                <Text>Loading users…</Text>
              </Box>
            </Center>
          )}

          {query.error && (
            <Alert color="red" title="Failed to load users">
              {query.error.message}
            </Alert>
          )}

          {data && (
            <Card withBorder padding="md" radius="md">
              <Group justify="space-between" mb="sm">
                <Text size="sm" c="dimmed">
                  {search ? `Search results for "${search}"` : `Showing ${rows.length} of ${total}`}
                </Text>
                {hasPagination && (
                  <Group gap="xs">
                    <Button
                      size="xs"
                      variant="default"
                      onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                      disabled={offset === 0}
                    >
                      Prev
                    </Button>
                    <Text size="xs" c="dimmed">
                      Offset {offset}
                    </Text>
                    <Button size="xs" variant="default" onClick={() => setOffset(offset + PAGE_SIZE)}>
                      Next
                    </Button>
                  </Group>
                )}
              </Group>

              {rows.length === 0 ? (
                <Text c="dimmed">{search ? "No user found with that email." : "No users."}</Text>
              ) : (
                <Table striped highlightOnHover withRowBorders={false}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Email</Table.Th>
                      <Table.Th>Tier</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Role</Table.Th>
                      <Table.Th>Gift Until</Table.Th>
                      <Table.Th>Created</Table.Th>
                      <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {rows.map((user) => (
                      <Table.Tr key={user.id}>
                        <Table.Td>
                          <Stack gap={0}>
                            <Text size="sm">{user.email ?? "—"}</Text>
                            <Code style={{ fontSize: 10 }}>{user.id.slice(0, 8)}…</Code>
                          </Stack>
                        </Table.Td>
                        <Table.Td>
                          <Badge color={tierBadgeColor(user.subscriptionTier, user.hasActiveGift)} variant="light">
                            {user.hasActiveGift ? `${user.giftedTier} (gift)` : (user.subscriptionTier ?? "free")}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">
                            {user.subscriptionStatus ?? "—"}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{user.role ?? "user"}</Text>
                        </Table.Td>
                        <Table.Td>
                          {user.hasActiveGift ? (
                            <Tooltip label={formatDateTime(user.giftedUntil)}>
                              <Text size="sm">{formatDate(user.giftedUntil)}</Text>
                            </Tooltip>
                          ) : (
                            <Text size="sm" c="dimmed">
                              —
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">
                            {formatDate(user.createdAt)}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs">
                            <Button
                              size="xs"
                              variant="light"
                              leftSection={<IconGift size={14} />}
                              onClick={() => openGrantFor(user)}
                            >
                              {user.hasActiveGift ? "Extend" : "Gift Pro"}
                            </Button>
                            {user.hasActiveGift && (
                              <Tooltip label="Revoke active gift">
                                <ActionIcon
                                  variant="subtle"
                                  color="red"
                                  loading={revokeMutation.isPending && revokeMutation.variables === user.id}
                                  onClick={() => handleRevoke(user)}
                                >
                                  <IconTrash size={16} />
                                </ActionIcon>
                              </Tooltip>
                            )}
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Card>
          )}
        </Container>
      </AppShell.Main>

      <Modal
        opened={grantModalOpen}
        onClose={closeGrantModal}
        title={grantTarget?.hasActiveGift ? "Extend Pro gift" : "Grant Pro gift"}
        centered
      >
        {grantTarget && (
          <Stack>
            <Text size="sm">
              Recipient: <strong>{grantTarget.email ?? grantTarget.id}</strong>
            </Text>
            {grantTarget.hasActiveGift && grantTarget.giftedUntil && (
              <Alert color="violet" variant="light">
                Current gift expires {formatDateTime(grantTarget.giftedUntil)}. Granting again replaces the expiration
                with a new {grantDays}-day window starting now.
              </Alert>
            )}
            <NumberInput
              label="Duration (days)"
              min={1}
              max={365}
              value={grantDays}
              onChange={(v) => setGrantDays(typeof v === "number" ? v : 30)}
            />
            <Textarea
              label="Reason (optional)"
              placeholder="e.g. early-beta supporter"
              value={grantReason}
              onChange={(e) => setGrantReason(e.currentTarget.value)}
              autosize
              minRows={2}
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={closeGrantModal}>
                Cancel
              </Button>
              <Button onClick={handleConfirmGrant} loading={grantMutation.isPending}>
                {grantTarget.hasActiveGift ? "Extend gift" : "Grant gift"}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </AppShell>
  )
}
